<template>
  <BaseWidget
    title="Assignments ending soon"
    :show-info-icon="true"
    info-tooltip="Assignments with end dates coming up within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/5321000-what-is-an-assignment', text: 'Learn about Assignments' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    custom-class="assignments-ending-soon-widget"
    :date-range="dateRange"
    :future-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      Error loading assignments: {{ error.message }}
    </div>
    <template v-else>
      <div
        ref="widgetRef"
        :class="['widget-content-wrapper', { 'focused': focused }]"
      >
        <div class="assignment-count">
          <div class="count-row">
            <span class="count">{{ assignmentCount }}</span>
            <ComparisonChip 
              :change="changePercentage" 
              :previous-value="formattedPreviousCount"
              :previous-period-dates="previousTimestamps"
              :inverted="true"
              :date-range-preset="dateRange"
              label="Assignments ending soon"
            />
          </div>
          <div class="count-label-row">
            <span class="count-label">{{ dateRangeLabel }}</span>
            <a
              v-if="assignments.length > 0"
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </div>
        
        <div
          v-if="!focused"
          class="assignments-list"
        >
          <div 
            v-for="(assignment, index) in displayAssignments" 
            :key="assignment.id || index" 
            class="assignment-item"
            @click="handleAssignmentClickGuarded(assignment)"
          >
            <div class="assignment-row">
              <div class="assignment-image">
                <img 
                  v-if="assignment.image" 
                  :src="assignment.image" 
                  :alt="assignment.name"
                >
                <div
                  v-else
                  class="image-placeholder"
                >
                  {{ getInitials(assignment.name) }}
                </div>
              </div>
              <div class="assignment-details">
                <div class="assignment-name">
                  {{ assignment.name }}
                </div>
                <div class="assignment-meta">
                  <span class="assignment-time">{{ formatAssignmentTime(assignment) }}</span>
                  <span
                    v-if="assignment.user?.name"
                    class="assignment-meta-sep"
                  > • </span>
                  <span
                    v-if="assignment.user?.name"
                    class="assignment-account-name"
                  >{{ assignment.user.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="displayAssignments.length === 0 && assignments.length === 0 && !hasMoreAssignments"
            class="no-assignments"
          >
            No assignments ending soon
          </div>
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'

import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps, DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRangeDates } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { getResourceImageUrl } from '../utils/resourceImage.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { getDemoAssignmentsEndingSoonData } from '../services/demoData.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'next_7_days')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Next 7 days'
})

// Get location IDs from parent window URL
const locationIds = useLocationFilter()

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Calculate timestamps from date range
// Extend to_timestamp by 1 day because the API stores assignment end dates as
// midnight of the following day (e.g. "ends Apr 13" → Apr 14 00:00:00 local time)
const timestamps = computed(() => {
  const ts = getDateRangeTimestamps(dateRange.value, referenceDate.value)
  return {
    ...ts,
    to_timestamp: ts.to_timestamp + 86400 // +1 day in seconds
  }
})

// Query assignments that END within the date range (containment, not overlap)
const assignmentsQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      end_timestamp_from: $from_timestamp
      end_timestamp_to: $to_timestamp
      limit: $limit
      page: $page
      type: ASSIGNMENT
      status: [ACTIVE, UPCOMING]
    ) {
      total
      data {
        assignment_id
        title
        status
        start_timestamp
        end_timestamp
        resource {
          resource_id
          name
          main_image {
            url
            __typename
          }
          location {
            location_id
            name
            timezone
            timezone_name
            __typename
          }
          __typename
        }
        location {
          location_id
          name
          timezone
          timezone_name
          __typename
        }
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'assignments_ending_display',
  query: assignmentsQuery,
  variables: computed(() => ({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp,
    limit: 3,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoAssignmentsEndingSoonData({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp
  })
})

// Fetch previous period data for comparison
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
})

const previousQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      end_timestamp_from: $from_timestamp
      end_timestamp_to: $to_timestamp
      limit: $limit
      page: $page
      type: ASSIGNMENT
      status: [ACTIVE, UPCOMING]
    ) {
      total
      __typename
    }
  }
`

const {
  data: previousData,
  loading: previousLoading,
  error: previousError,
  cleanup: previousCleanup,
  refresh: refreshPrevious
} = useGraphQLData({
  dataKey: 'assignments_ending_previous',
  query: previousQuery,
  variables: computed(() => ({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp,
    limit: 1,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoAssignmentsEndingSoonData({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp
  })
})

// Ensure previous data is fetched when current data loads
watch(() => data.value, (newData) => {
  if (newData && !previousLoading.value && !previousData.value) {
    refreshPrevious(true)
  }
}, { immediate: true })

// Map schedule data for display (API already filters by end_timestamp containment)
const assignments = computed(() => {
  const raw = data.value?.schedule
  const ts = timestamps.value
  if (!data.value?.schedule?.data) {
    return []
  }

  return data.value.schedule.data
    .filter(item => !item.status || ['ACTIVE', 'UPCOMING'].includes(item.status.toUpperCase()))
    .map(item => {
      // Get timezone from location (prefer direct location, fallback to resource location)
      const location = item.location || item.resource?.location
      const timezone = location?.timezone || null
      
      return {
        id: item.assignment_id,
        name: item.title || item.resource?.name || 'Unnamed Assignment',
        endTime: item.end_timestamp * 1000, // Convert to milliseconds
        startTime: item.start_timestamp * 1000,
        image: getResourceImageUrl(item.resource?.main_image?.url, item.resource?.name || item.title || 'Assignment'),
        location: location?.name || null,
        timezone: timezone, // Include timezone for formatting
        user: item.owner_account ? {
          name: item.owner_account.name || null,
          avatar: getAvatarUrl(item.owner_account.avatar?.url, item.owner_account.name || item.owner_account.account_id)
        } : null
      }
    })
    .sort((a, b) => a.endTime - b.endTime) // Sort by end time (soonest ending first)
})

// Count assignments ending in the selected date range (API total is accurate with containment params)
const assignmentCount = computed(() => {
  return data.value?.schedule?.total ?? assignments.value.length
})

// Count assignments ending in previous period
const previousCount = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  return previousData.value?.schedule?.total ?? 0
})

// Calculate percentage change
const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  const current = assignmentCount.value
  const previous = previousCount.value
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change: ((current - previous) / previous) * 100
  // Normal calculation - color inversion is handled by inverted prop in ComparisonChip
  const change = ((current - previous) / previous) * 100
  const rounded = Math.round(change * 10) / 10
  
  return rounded
})

// Format previous count for display
const formattedPreviousCount = computed(() => {
  if (previousCount.value === null || previousCount.value === undefined) return null
  return previousCount.value.toString()
})

// Calculate number of rows to display based on widget height (64px rows; leave room for "View more")
// Pattern: 2-row→2 slots, 3-row→4, 4-row→6, 5-row→8, 6-row→10... i.e. 2*(rowSpan-1)
const MAX_DISPLAY_ROWS = 3

// Check if there are more assignments than we can display (only show when rowSpan > 1)
const hasMoreAssignments = computed(() => {
  return props.rowSpan > 1 && assignmentCount.value > MAX_DISPLAY_ROWS
})

// Calculate remaining count
const remainingAssignmentsCount = computed(() => {
  if (!hasMoreAssignments.value) return 0
  return assignmentCount.value - displayAssignments.value.length
})

// Display up to 3 assignments, always
const displayAssignments = computed(() => {
  if (props.rowSpan === 1) return []
  return assignments.value.slice(0, MAX_DISPLAY_ROWS)
})

// Format assignment end time for display using the location's timezone
const formatAssignmentTime = (assignment) => {
  try {
    if (!assignment?.endTime) return ''
    
    // Use the location's timezone if available and valid, otherwise fall back to browser timezone
    const timezone = (assignment.timezone && typeof assignment.timezone === 'string' && assignment.timezone.trim()) || undefined
    
    const dateFormatterForComparison = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone
  })
  
  const assignmentDate = new Date(assignment.endTime)
  
  // Get date parts in the location's timezone for comparison
  const assignmentDateParts = dateFormatterForComparison.formatToParts(assignmentDate)
  const assignmentYear = parseInt(assignmentDateParts.find(p => p.type === 'year').value)
  const assignmentMonth = parseInt(assignmentDateParts.find(p => p.type === 'month').value) - 1 // Month is 0-indexed
  const assignmentDay = parseInt(assignmentDateParts.find(p => p.type === 'day').value)
  
  // Get today's date in the location's timezone
  const now = new Date()
  const todayParts = dateFormatterForComparison.formatToParts(now)
  const todayYear = parseInt(todayParts.find(p => p.type === 'year').value)
  const todayMonth = parseInt(todayParts.find(p => p.type === 'month').value) - 1
  const todayDay = parseInt(todayParts.find(p => p.type === 'day').value)
  
  const assignmentDateObj = new Date(assignmentYear, assignmentMonth, assignmentDay)
  const todayDateObj = new Date(todayYear, todayMonth, todayDay)
  
  const diffTime = assignmentDateObj - todayDateObj
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return 'Ends today'
  } else if (diffDays === 1) {
    return 'Ends tomorrow'
  } else if (diffDays > 0) {
    return `Ends in ${diffDays} days`
  } else {
    return `Ended ${Math.abs(diffDays)} days ago`
  }
  } catch (e) {
    console.warn('AssignmentsEndingSoonWidget formatAssignmentTime error:', e)
    return ''
  }
}

// Get initials for placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  }
}

// Handle "View all" click - navigate to schedule/meetings via parent window
const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const dates = getDateRangeDates(dateRange.value)
  const navigationUrl = `/schedule/list?event_types=ASSIGNMENT&date_from=${dates.from}&date_to=${dates.to}`

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (e) {
    console.error('❌ Error posting ReplaceUrl message:', e)
  }
}

// Handle individual assignment click - open assignment panel via parent window
// Uses ViewDetails command format matching the working Optix scheduler
const handleAssignmentClickGuarded = guard((assignment) => {
  const params = getUrlParams()
  
  if (!assignment.id) {
    console.warn('⚠️ Assignment ID not found:', assignment)
    if (process.env.NODE_ENV === 'development') {
      alert('Error: Assignment ID is missing')
    }
    return
  }
  
  // Extract assignment_id - handle both direct id and prefixed formats (e.g., "ASSIGNMENT-123" → "123")
  let assignmentId = assignment.id
  if (assignmentId.includes('-')) {
    // If id has a prefix like "ASSIGNMENT-123", extract just the number
    const parts = assignmentId.split('-')
    assignmentId = parts[parts.length - 1]
  }
  
  
  // Use ViewDetails command format (matching working Optix scheduler)
  // Command: "ViewDetails"
  // Payload: { assignment_id: "...", canvasUUID: "..." }
  const message = {
    command: "ViewDetails",
    payload: {
      assignment_id: assignmentId,
      canvasUUID: params.canvasUUID || "xxx" // Use actual canvasUUID if available, otherwise "xxx" as placeholder
    }
  }
  
  
  try {
    // Send message to parent Optix window (matching working app pattern)
    window.parent.postMessage(message, "*")
  } catch (error) {
    console.error('❌ Error posting message:', error)
    if (process.env.NODE_ENV === 'development') {
      alert(`Error posting message: ${error.message}`)
    }
  }
  
  // Note: canvasUUID is currently using placeholder "xxx" if not in URL
  // The parent window should handle the ViewDetails command and open the assignment panel
  if (!params.canvasUUID) {
    console.warn('⚠️ canvasUUID not found in URL - using placeholder "xxx"')
    console.warn('💡 Update canvasUUID extraction if it\'s available from another source')
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
:deep(.assignments-ending-soon-widget) {
  cursor: pointer;
  overflow: hidden;
}

:deep(.assignments-ending-soon-widget .widget-content) {
  justify-content: flex-start;
  overflow: hidden;
}


.assignment-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 6px;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding-bottom: 14px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.widget-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.assignments-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.assignment-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.assignment-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.assignment-item:last-child {
  border-bottom: none;
}

.assignment-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.assignment-item-more {
  min-height: 32px;
  padding: 4px 4px;
}

.assignment-item-more:hover {
  background-color: transparent;
}

.assignment-item-more .assignment-row {
  width: 100%;
}

.assignment-item-more .assignment-row-more {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.assignment-item-more .view-more-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.assignment-item-more .view-more-link:hover {
  color: #2563eb;
}

.assignment-item-more .assignment-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.assignment-item-more .assignment-name {
  margin-bottom: 0;
}

.assignment-item-more .assignment-time {
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
}

.assignment-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.assignment-image {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.assignment-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.assignment-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.assignment-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.assignment-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assignment-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 4px;
}

.assignment-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.assignment-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-assignments {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.assignment-image-more {
  position: relative;
  width: 36px;
  height: 28px;
  background: transparent;
}

.stacked-images {
  position: relative;
  width: 100%;
  height: 100%;
}

.stacked-image {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid white;
}

.stacked-image-top {
  top: 0;
  left: 0;
  z-index: 2;
  transform: rotate(-8deg);
}

.stacked-image-bottom {
  top: 0;
  left: 7px;
  z-index: 1;
}

.stacked-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>
